import type { Row } from "./api";

/** Local interview adapter only. It neither certifies filing status nor changes a source contract. */
export const FILING_INTERVIEW_VERSION = 2 as const;
export const FILING_STATUS_ITEMS = [
  "SC-MARRIAGE",
  "SC-SPOUSE-DEATH",
  "SC-HOUSEHOLD",
  "SC-RDP-COMMUNITY",
  "SC-DEPENDENCY",
] as const;
export type FilingStatusItem = (typeof FILING_STATUS_ITEMS)[number];
export type FilingStatusResponse =
  "PRESENT" | "ABSENT" | "UNKNOWN" | "CONFLICT" | "NOT_APPLICABLE";
export type MaritalFact = "UNMARRIED" | "MARRIED" | "OTHER";
export const RETURN_BUILDER_PROFILE_IDS = [
  "employee",
  "business",
  "gig",
  "investments",
  "homeowner",
  "dependents",
  "education",
  "charity",
  "rental",
  "retirement",
] as const;
export type ReturnBuilderProfileId =
  (typeof RETURN_BUILDER_PROFILE_IDS)[number];
export type ReturnBuilderDraft = {
  version: 1;
  profile: {
    version: 1;
    selected: ReturnBuilderProfileId[];
    confirmed: boolean;
  };
  taxpayer?: { ssn?: string; county?: string };
};
export type FilingInterview = {
  version: 1 | 2;
  filingStatus?: "SINGLE" | "MFJ" | "MFS" | "HOH" | "QSS" | "OTHER";
  maritalSituation?:
    | "NEVER_MARRIED"
    | "DIVORCED_OR_SEPARATED"
    | "WIDOWED"
    | "MARRIED"
    | "UNSURE";
  partnership?: "YES" | "NO" | "UNSURE";
  spouseDeathYear?: "2025" | "2024" | "2023" | "EARLIER" | "UNSURE";
  recentSpouseDeath?: "YES" | "NO" | "UNSURE";
  communityIncome?: "YES" | "NO" | "UNSURE";
  spouseName?: string;
  spouseTaxId?: string;
  spouseIdStatus?: "HAS_ID" | "NO_US_TIN" | "UNSURE";
  spouseResidency?:
    "US_CITIZEN_OR_RESIDENT_ALL_YEAR" | "NONRESIDENT_OR_DUAL_STATUS" | "UNSURE";
  spouseResidentElection?: "YES" | "NO" | "UNSURE";
  jointReturnConsent?: "YES" | "NO" | "UNSURE";
  spouseItemizes?: "YES" | "NO" | "UNSURE";
  livedWithSpouse?: "YES" | "NO" | "UNSURE";
  spouseAbsentLastSixMonths?: "YES" | "NO" | "UNSURE";
  separateReturn?: "YES" | "NO" | "UNSURE";
  paidMoreThanHalfHomeCosts?: "YES" | "NO" | "UNSURE";
  qualifyingPersonName?: string;
  qualifyingPersonRelationship?:
    | "CHILD"
    | "FOSTER_CHILD"
    | "PARENT"
    | "OTHER_RELATIVE"
    | "UNRELATED"
    | "UNSURE";
  qualifyingPersonResidence?:
    | "ALL_YEAR"
    | "MORE_THAN_HALF"
    | "HALF_OR_LESS"
    | "EXCEPTION_REVIEW"
    | "UNSURE";
  qualifyingPersonClaim?:
    | "CLAIMED"
    | "CUSTODIAL_PARENT_RELEASE"
    | "EXCEPTION_REVIEW"
    | "NOT_CLAIMED"
    | "UNSURE";
  jointReturnEligibleInDeathYear?: "YES" | "NO" | "UNSURE";
  returnBuilder?: ReturnBuilderDraft;
  reviewedPrevious?: string;
  managed?: {
    items: Partial<Record<FilingStatusItem, FilingStatusResponse>>;
    maritalFact?: MaritalFact;
  };
};
export type FilingDerivation = {
  target: string;
  value: unknown;
  inputs: Record<string, unknown>;
};
export type FilingConflict = {
  target: string;
  message: string;
  previous: unknown;
  proposed: unknown;
};
export type FilingDerivationResult = {
  answer: unknown;
  maritalFact?: MaritalFact;
  derivations: FilingDerivation[];
  conflicts: FilingConflict[];
};

const object = (value: unknown): value is Row =>
  value !== null && typeof value === "object" && !Array.isArray(value);
const own = (value: unknown, key: string): boolean =>
  object(value) && Object.prototype.hasOwnProperty.call(value, key);
const member = (value: unknown, allowed: readonly string[]) =>
  typeof value === "string" && allowed.includes(value);
const responses = [
  "PRESENT",
  "ABSENT",
  "UNKNOWN",
  "CONFLICT",
  "NOT_APPLICABLE",
];
const choiceFields: Record<string, readonly string[]> = {
  filingStatus: ["SINGLE", "MFJ", "MFS", "HOH", "QSS", "OTHER"],
  maritalSituation: [
    "NEVER_MARRIED",
    "DIVORCED_OR_SEPARATED",
    "WIDOWED",
    "MARRIED",
    "UNSURE",
  ],
  partnership: ["YES", "NO", "UNSURE"],
  spouseDeathYear: ["2025", "2024", "2023", "EARLIER", "UNSURE"],
  recentSpouseDeath: ["YES", "NO", "UNSURE"],
  communityIncome: ["YES", "NO", "UNSURE"],
  spouseIdStatus: ["HAS_ID", "NO_US_TIN", "UNSURE"],
  spouseResidency: [
    "US_CITIZEN_OR_RESIDENT_ALL_YEAR",
    "NONRESIDENT_OR_DUAL_STATUS",
    "UNSURE",
  ],
  spouseResidentElection: ["YES", "NO", "UNSURE"],
  jointReturnConsent: ["YES", "NO", "UNSURE"],
  spouseItemizes: ["YES", "NO", "UNSURE"],
  livedWithSpouse: ["YES", "NO", "UNSURE"],
  spouseAbsentLastSixMonths: ["YES", "NO", "UNSURE"],
  separateReturn: ["YES", "NO", "UNSURE"],
  paidMoreThanHalfHomeCosts: ["YES", "NO", "UNSURE"],
  qualifyingPersonRelationship: [
    "CHILD",
    "FOSTER_CHILD",
    "PARENT",
    "OTHER_RELATIVE",
    "UNRELATED",
    "UNSURE",
  ],
  qualifyingPersonResidence: [
    "ALL_YEAR",
    "MORE_THAN_HALF",
    "HALF_OR_LESS",
    "EXCEPTION_REVIEW",
    "UNSURE",
  ],
  qualifyingPersonClaim: [
    "CLAIMED",
    "CUSTODIAL_PARENT_RELEASE",
    "EXCEPTION_REVIEW",
    "NOT_CLAIMED",
    "UNSURE",
  ],
  jointReturnEligibleInDeathYear: ["YES", "NO", "UNSURE"],
};
const textFields: Record<string, number> = {
  spouseName: 200,
  spouseTaxId: 20,
  qualifyingPersonName: 200,
};
const legacyFields = new Set([
  "version",
  "filingStatus",
  "maritalSituation",
  "partnership",
  "spouseDeathYear",
  "recentSpouseDeath",
  "communityIncome",
  "reviewedPrevious",
  "managed",
]);
const clone = <T>(value: T): T =>
  value === undefined ? value : JSON.parse(JSON.stringify(value));

/** UI draft shape only. These fields are not identity verification, tax facts, or profile-derived admission answers. */
export function validReturnBuilderDraft(
  value: unknown,
): value is ReturnBuilderDraft {
  if (
    !object(value) ||
    value.version !== 1 ||
    !object(value.profile) ||
    Object.keys(value).some(
      (key) => !["version", "profile", "taxpayer"].includes(key),
    )
  )
    return false;
  const profile = value.profile;
  if (
    profile.version !== 1 ||
    !Array.isArray(profile.selected) ||
    typeof profile.confirmed !== "boolean" ||
    Object.keys(profile).some(
      (key) => !["version", "selected", "confirmed"].includes(key),
    ) ||
    profile.selected.length > RETURN_BUILDER_PROFILE_IDS.length ||
    new Set(profile.selected).size !== profile.selected.length ||
    profile.selected.some(
      (id: unknown) => !member(id, RETURN_BUILDER_PROFILE_IDS),
    )
  )
    return false;
  if (ownsBuilderKey(value, "taxpayer")) {
    if (
      !object(value.taxpayer) ||
      Object.keys(value.taxpayer).some(
        (key) => !["ssn", "county"].includes(key),
      )
    )
      return false;
    for (const [key, max] of [
      ["ssn", 20],
      ["county", 100],
    ] as const)
      if (
        ownsBuilderKey(value.taxpayer, key) &&
        (typeof value.taxpayer[key] !== "string" ||
          value.taxpayer[key].length > max ||
          /[\u0000-\u001f\u007f-\u009f]/.test(value.taxpayer[key]))
      )
        return false;
  }
  return true;
}
const ownsBuilderKey = (value: Row, key: string) =>
  Object.prototype.hasOwnProperty.call(value, key);

/** Structural validation is intentionally not an eligibility or completeness decision. */
export function filingInterviewValid(value: unknown): value is FilingInterview {
  if (!object(value) || ![1, 2].includes(value.version)) return false;
  if (
    value.version === 1 &&
    (Object.keys(value).some((key) => !legacyFields.has(key)) ||
      (value.filingStatus !== undefined &&
        !member(value.filingStatus, ["SINGLE", "OTHER"])))
  )
    return false;
  if (
    Object.keys(value).some(
      (key) =>
        key !== "version" &&
        key !== "managed" &&
        key !== "reviewedPrevious" &&
        key !== "returnBuilder" &&
        !own(choiceFields, key) &&
        !own(textFields, key),
    )
  )
    return false;
  if (
    own(value, "returnBuilder") &&
    !validReturnBuilderDraft(value.returnBuilder)
  )
    return false;
  for (const [key, values] of Object.entries(choiceFields))
    if (
      own(value, key) &&
      value[key] !== undefined &&
      !member(value[key], values)
    )
      return false;
  for (const [key, max] of Object.entries(textFields))
    if (
      own(value, key) &&
      value[key] !== undefined &&
      (typeof value[key] !== "string" ||
        value[key].length > max ||
        /[\u0000-\u001f\u007f-\u009f]/.test(value[key]))
    )
      return false;
  if (
    own(value, "reviewedPrevious") &&
    value.reviewedPrevious !== undefined &&
    (typeof value.reviewedPrevious !== "string" ||
      value.reviewedPrevious.length > 100_000)
  )
    return false;
  if (own(value, "managed") && value.managed !== undefined) {
    const managed = value.managed;
    if (
      !object(managed) ||
      !object(managed.items) ||
      Object.keys(managed).some(
        (key) => key !== "items" && key !== "maritalFact",
      )
    )
      return false;
    if (
      Object.entries(managed.items).some(
        ([key, item]) =>
          !FILING_STATUS_ITEMS.includes(key as FilingStatusItem) ||
          !member(item, responses),
      )
    )
      return false;
    if (
      own(managed, "maritalFact") &&
      !member(managed.maritalFact, ["UNMARRIED", "MARRIED", "OTHER"])
    )
      return false;
  }
  return true;
}

/** Metadata is outside the frozen source answer and must be removed before source validation. */
export function stripFilingInterview(raw: unknown): unknown {
  if (!object(raw)) return clone(raw);
  const result = clone(raw);
  delete result.interview;
  return result;
}

function known(raw: unknown): Row | undefined {
  if (!object(raw)) return undefined;
  if (own(raw, "state"))
    return raw.state === "KNOWN" && object(raw.value) ? raw.value : undefined;
  return raw;
}

/** Reading never creates a saved answer or reconstructs marital history from an absence checklist. */
export function parseFilingInterview(raw: unknown): FilingInterview {
  if (object(raw) && filingInterviewValid(raw.interview))
    return clone(raw.interview);
  const existing = known(raw)?.filing_status;
  return member(existing, ["SINGLE", "OTHER"])
    ? { version: 1, filingStatus: existing }
    : { version: 1 };
}

function inputsOf(interview: FilingInterview): Row {
  return Object.fromEntries(
    [...Object.keys(choiceFields), ...Object.keys(textFields)]
      .filter(
        (key) => own(interview, key) && (interview as Row)[key] !== undefined,
      )
      .map((key) => [key, (interview as Row)[key]]),
  );
}

function canonical(value: unknown): string {
  if (value === undefined) return "null";
  if (value === null || typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(canonical).join(",")}]`;
  return `{${Object.keys(value)
    .sort()
    .filter((key) => (value as Row)[key] !== undefined)
    .map((key) => `${JSON.stringify(key)}:${canonical((value as Row)[key])}`)
    .join(",")}}`;
}

/** Local concurrency/intent fingerprint, not a cryptographic attestation or tax evidence. */
export function filingReviewToken(
  previousRaw: unknown,
  selected: Row,
  interview: FilingInterview,
): string {
  return `filing-review-v1:${canonical({
    returnId: selected.id,
    returnRevision: selected.revision,
    previous: stripFilingInterview(previousRaw),
    marital: selected.facts?.["taxpayer.marital_status_year_end"],
    dependency: selected.answers?.["Q-009"],
    dependencyReview: selected.facts?.["taxpayer.dependency_tests"],
    inputs: inputsOf(interview),
  })}`;
}

const itemLabels: Record<FilingStatusItem, string> = {
  "SC-MARRIAGE": "Marital status",
  "SC-SPOUSE-DEATH": "A spouse's death",
  "SC-HOUSEHOLD": "Household and filing-status circumstances",
  "SC-RDP-COMMUNITY": "Domestic partnership or community income",
  "SC-DEPENDENCY": "Dependents and support arrangements",
};
const nonnegativeInteger = (value: unknown): value is number =>
  typeof value === "number" && Number.isSafeInteger(value) && value >= 0;

/** Called after an explicit interview edit/save, never during render to persist defaults.
 * Single alone clears nothing. Inputs supersede only this adapter's unchanged prior outputs;
 * independent saved information requires a fresh, explicit reconciliation acknowledgment.
 */
export function deriveFilingInterview(
  interview: FilingInterview,
  selected: Row,
  previousRaw: unknown,
): FilingDerivationResult {
  if (!filingInterviewValid(interview))
    return {
      answer: clone(previousRaw),
      derivations: [],
      conflicts: [
        {
          target: "filing-interview",
          previous: undefined,
          proposed: undefined,
          message:
            "Filing Status — Saved interview: This entry has an unsupported format. Ask an administrator to review it before making changes.",
        },
      ],
    };
  const oldValue =
    object(previousRaw) && object(previousRaw.value)
      ? previousRaw.value
      : known(previousRaw);
  const oldItems = object(oldValue?.items) ? oldValue.items : {};
  const priorMetadata =
    object(previousRaw) && filingInterviewValid(previousRaw.interview)
      ? previousRaw.interview
      : undefined;
  const confirmed =
    interview.reviewedPrevious ===
    filingReviewToken(previousRaw, selected, interview);
  const conflicts: FilingConflict[] = [];
  const derivations: FilingDerivation[] = [];
  const proposed = Object.fromEntries(
    FILING_STATUS_ITEMS.map((id) => [id, "UNKNOWN"]),
  ) as Record<FilingStatusItem, FilingStatusResponse>;
  const sourceInputs: Partial<
    Record<FilingStatusItem, Record<string, unknown>>
  > = {};
  const put = (
    id: FilingStatusItem,
    value: FilingStatusResponse,
    inputs: Record<string, unknown>,
  ) => {
    proposed[id] = value;
    sourceInputs[id] = inputs;
  };
  const situation = interview.maritalSituation;
  let marital: MaritalFact | undefined;
  if (situation === "NEVER_MARRIED" || situation === "DIVORCED_OR_SEPARATED") {
    marital = "UNMARRIED";
    put("SC-MARRIAGE", "ABSENT", { maritalSituation: situation });
  } else if (situation === "MARRIED") {
    marital = "MARRIED";
    put("SC-MARRIAGE", "PRESENT", { maritalSituation: situation });
  } else if (situation === "UNSURE") marital = "OTHER";
  if (situation === "NEVER_MARRIED")
    put("SC-SPOUSE-DEATH", "ABSENT", { maritalSituation: situation });
  if (situation === "DIVORCED_OR_SEPARATED") {
    if (
      interview.recentSpouseDeath === "YES" ||
      interview.recentSpouseDeath === "NO"
    )
      put(
        "SC-SPOUSE-DEATH",
        interview.recentSpouseDeath === "YES" ? "PRESENT" : "ABSENT",
        {
          maritalSituation: situation,
          recentSpouseDeath: interview.recentSpouseDeath,
        },
      );
  }
  if (situation === "WIDOWED") {
    const deathYear = interview.spouseDeathYear;
    if (deathYear && deathYear !== "UNSURE") {
      // The UI must explicitly define WIDOWED as not remarried. A 2025 death retains married treatment.
      marital = deathYear === "2025" ? "MARRIED" : "UNMARRIED";
      put("SC-MARRIAGE", deathYear === "2025" ? "PRESENT" : "ABSENT", {
        maritalSituation: situation,
        spouseDeathYear: deathYear,
      });
      put("SC-SPOUSE-DEATH", deathYear === "EARLIER" ? "ABSENT" : "PRESENT", {
        maritalSituation: situation,
        spouseDeathYear: deathYear,
      });
    }
  }
  if (interview.communityIncome === "YES")
    put("SC-RDP-COMMUNITY", "PRESENT", { communityIncome: "YES" });
  else if (interview.partnership === "YES")
    put("SC-RDP-COMMUNITY", "PRESENT", { partnership: "YES" });
  else if (interview.communityIncome === "UNSURE")
    put("SC-RDP-COMMUNITY", "UNKNOWN", { communityIncome: "UNSURE" });
  else if (situation === "NEVER_MARRIED" && interview.partnership === "NO")
    put("SC-RDP-COMMUNITY", "ABSENT", {
      maritalSituation: situation,
      partnership: "NO",
    });
  else if (
    situation &&
    situation !== "NEVER_MARRIED" &&
    situation !== "UNSURE"
  ) {
    if (interview.partnership === "NO" && interview.communityIncome === "NO")
      put("SC-RDP-COMMUNITY", "ABSENT", {
        maritalSituation: situation,
        partnership: "NO",
        communityIncome: "NO",
      });
  }

  const dependency = known(selected.answers?.["Q-009"]);
  if (dependency) {
    const dependent = dependency.dependent_count,
      qualifying = dependency.qualifying_person_count;
    if (
      (nonnegativeInteger(dependent) && dependent > 0) ||
      (nonnegativeInteger(qualifying) && qualifying > 0)
    )
      put("SC-HOUSEHOLD", "PRESENT", {
        "Q-009.dependent_count": dependent,
        "Q-009.qualifying_person_count": qualifying,
      });
    if (
      dependency.claimable_status === true ||
      (nonnegativeInteger(dependent) && dependent > 0) ||
      (nonnegativeInteger(qualifying) && qualifying > 0)
    )
      put("SC-DEPENDENCY", "PRESENT", {
        "Q-009.claimable_status": dependency.claimable_status,
        "Q-009.dependent_count": dependent,
        "Q-009.qualifying_person_count": qualifying,
      });
    // Zero counts alone do not establish that support, custody, release and other dependency tests are resolved.
    // A client-side review artifact reference cannot authenticate the completed review or its evidence.
    const mismatches = [
      "claimable_status",
      "dependent_count",
      "qualifying_person_count",
    ].filter(
      (key) =>
        own(dependency, key) &&
        own(selected.facts, `taxpayer.${key}`) &&
        selected.facts[`taxpayer.${key}`] !== dependency[key],
    );
    if (mismatches.length) {
      const proof = Object.fromEntries(
        mismatches.flatMap((key) => [
          [`Q-009.${key}`, dependency[key]],
          [`taxpayer.${key}`, selected.facts[`taxpayer.${key}`]],
        ]),
      );
      put("SC-DEPENDENCY", "CONFLICT", proof);
      if (mismatches.some((key) => key !== "claimable_status"))
        put("SC-HOUSEHOLD", "CONFLICT", proof);
      for (const key of mismatches)
        conflicts.push({
          target: `taxpayer.${key}`,
          previous: clone(selected.facts[`taxpayer.${key}`]),
          proposed: clone(dependency[key]),
          message: `Dependents — ${key === "claimable_status" ? "Whether another taxpayer can claim this person" : key === "dependent_count" ? "Number of dependents" : "Number of qualifying people"}: The earlier saved entry differs from the Dependents answer. Reconcile those entries before treating filing status as resolved.`,
        });
    }
  }

  // A requested household-based status is a review trigger, not evidence that its tests are satisfied.
  if (
    (interview.filingStatus === "HOH" || interview.filingStatus === "QSS") &&
    proposed["SC-HOUSEHOLD"] !== "CONFLICT"
  )
    put("SC-HOUSEHOLD", "PRESENT", {
      requestedFilingStatus: interview.filingStatus,
    });

  const managedItems: Partial<Record<FilingStatusItem, FilingStatusResponse>> =
    {};
  const items: Row = clone(oldItems);
  for (const id of FILING_STATUS_ITEMS) {
    const old = oldItems[id],
      next = proposed[id];
    const owned =
      member(old, responses) && priorMetadata?.managed?.items[id] === old;
    const independent = old !== undefined && old !== "UNKNOWN" && !owned;
    if (independent && next === "UNKNOWN" && old !== "CONFLICT") {
      // No new conclusion was reached. Retain independently supplied resolved information without adopting it.
      items[id] = old;
    } else if (independent && old !== next && !confirmed) {
      items[id] = old;
      conflicts.push({
        target: id,
        previous: clone(old),
        proposed: next,
        message: `Filing Status — ${itemLabels[id]}: Earlier saved information differs from these answers. Review the earlier entry before replacing it; it has been preserved.`,
      });
    } else {
      items[id] = next;
      managedItems[id] = next;
      derivations.push({
        target: id,
        value: next,
        inputs: clone(sourceInputs[id] ?? {}),
      });
    }
  }
  const suppliedMarital = selected.facts?.["taxpayer.marital_status_year_end"];
  const previousManagedMarital = priorMetadata?.managed?.maritalFact;
  if (
    !marital &&
    previousManagedMarital &&
    suppliedMarital === previousManagedMarital
  )
    marital = "OTHER";
  let maritalFact: MaritalFact | undefined;
  let managedMarital: MaritalFact | undefined;
  if (marital) {
    if (
      suppliedMarital !== undefined &&
      suppliedMarital !== marital &&
      suppliedMarital !== previousManagedMarital &&
      !confirmed
    )
      conflicts.push({
        target: "taxpayer.marital_status_year_end",
        previous: clone(suppliedMarital),
        proposed: marital,
        message:
          "Filing Status — Marital status: The earlier saved marital status differs from these answers. Review it before replacing it; the earlier entry has been preserved.",
      });
    else {
      maritalFact = marital;
      managedMarital = marital;
      derivations.push({
        target: "taxpayer.marital_status_year_end",
        value: marital,
        inputs: {
          maritalSituation: situation ?? null,
          ...(situation === "WIDOWED"
            ? { spouseDeathYear: interview.spouseDeathYear ?? null }
            : {}),
        },
      });
    }
  }
  if (interview.filingStatus === "SINGLE" && marital === "MARRIED")
    conflicts.push({
      target: "filingStatus",
      previous: "SINGLE",
      proposed: "OTHER",
      message:
        "Filing Status — Filing status: Single does not match the marital circumstances entered. Review the legal marital status and choose the applicable filing status.",
    });

  const metadata: FilingInterview = {
    version: 2,
    ...inputsOf(interview),
    ...(interview.returnBuilder
      ? { returnBuilder: clone(interview.returnBuilder) }
      : {}),
    ...(confirmed ? { reviewedPrevious: interview.reviewedPrevious } : {}),
    managed: {
      items: managedItems,
      ...(managedMarital ? { maritalFact: managedMarital } : {}),
    },
  };
  // Acknowledgment remains usable for draft/save reconciliation but is bound to this return revision and exact prior values.
  const stripped = stripFilingInterview(previousRaw);
  if (object(stripped) && stripped.state === "CONFLICT" && !confirmed) {
    conflicts.push({
      target: "Q-008",
      previous: clone(stripped),
      proposed: undefined,
      message:
        "Filing Status — Earlier filing-status information: Conflicting information is already saved. Review it before replacing the earlier answers.",
    });
    return {
      answer: { ...stripped, interview: metadata },
      derivations: [],
      conflicts,
    };
  }
  const value: Row = { ...(oldValue ? clone(oldValue) : {}), items };
  if (interview.filingStatus !== undefined)
    value.filing_status =
      interview.filingStatus === "SINGLE" ? "SINGLE" : "OTHER";
  else delete value.filing_status;
  const answer: Row = {
    ...(object(stripped) && own(stripped, "state") ? stripped : {}),
    state: "KNOWN",
    value,
    interview: metadata,
  };
  return {
    answer,
    ...(maritalFact ? { maritalFact } : {}),
    derivations,
    conflicts,
  };
}
